// Libs
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
// Selectors
import { selectProfile } from 'screens/Login/selectors';
// Utils
import TableUtils, {
	getStableTableRowKey,
	mergeStateViewerPermissions,
	notifyVisibleDatasourceChange,
} from './utils';
// Hooks
import { useTheme } from 'hooks';
// Components
import Wrapper from 'components/Wrapper/Wrapper';
import TableVirtualized from 'components/TableVirtualized';
import { LogoIcon } from 'components/Icons';
import RowRenderer from './RowRenderer';
import HeaderRenderer from './HeaderRenderer';
import CellRenderer from './CellRenderer';
import styled from 'styled-components';
import {
	selectSelectedDataFilterId,
	selectSelectedDataSourceConfig,
} from 'core/selectors';
import { selectSelectedSubModule } from 'components/Modules/selectors';
import {
	addTransactionSignatureActionField,
	isPendingTransactionsDatasource,
} from './transactionSignatureActionField';
import { addPaymentPaycutActionFields } from './paymentPaycutFields';
import BulkOrderActionsBar from './BulkOrderActionsBar';
import {
	isOrdersControlRootTable,
	prepareOrdersControlFields,
} from './ordersControlFields';

const ContainerTable = styled.div`
	overflow-x: ${props => (props.notHorizontalScroll ? 'hidden' : 'auto')};
	height: calc(100vh - 56px - ${({ $bottomInset = 0 }) => $bottomInset}px);
	&::-webkit-scrollbar {
		width: 5px;
		height: 10px;
	}
	overflow-y: hidden;
`;

const { Table, Column, Cell } = TableVirtualized;

const SELECTION_COLUMN_WIDTH = 48;

const getDefaultSelectionAriaLabel = (_rowData, itemId) =>
	`Seleccionar fila ${itemId ?? ''}`.trim();

const SelectionHeaderCheckbox = ({
	selectedItems,
	visibleItemIds,
	onChange,
	disabled,
}) => {
	const checkboxRef = useRef(null);
	const selectedIds = new Set(selectedItems);
	const selectedVisibleCount = visibleItemIds.filter(itemId =>
		selectedIds.has(itemId),
	).length;
	const checked =
		visibleItemIds.length > 0 && selectedVisibleCount === visibleItemIds.length;

	useEffect(() => {
		if (checkboxRef.current)
			checkboxRef.current.indeterminate = selectedVisibleCount > 0 && !checked;
	}, [checked, selectedVisibleCount]);

	return (
		<label title='Seleccionar todas las filas visibles'>
			<span
				style={{
					position: 'absolute',
					width: 1,
					height: 1,
					overflow: 'hidden',
				}}
			>
				Seleccionar todas las filas visibles
			</span>
			<input
				ref={checkboxRef}
				type='checkbox'
				checked={checked}
				disabled={disabled || visibleItemIds.length === 0}
				aria-label='Seleccionar todas las filas visibles'
				onChange={event => onChange(event.target.checked)}
			/>
		</label>
	);
};

const SelectionRowCheckbox = ({ checked, disabled, label, onChange }) => (
	<Cell>
		<input
			type='checkbox'
			checked={checked}
			disabled={disabled}
			aria-label={label}
			onClick={event => event.stopPropagation()}
			onChange={event => {
				event.stopPropagation();
				onChange(event.target.checked);
			}}
		/>
	</Cell>
);

const StandardTableScreen = ({
	isLoading,
	dataFilterLastRowId,
	datasourceFields,
	datasourcePaging = { totalLastRowId: 0 },
	datasource,
	unsortedDatasource,
	fieldActionTypes = {},
	setLastRowId,
	reloadDatasource,
	stateViewer = {},
	setStateItemIds = () => {},
	setSelectedItems = () => {},
	onlyItem = false,
	selectionKey = 'id',
	disableSelection = false,
	disableTransactionSignatureAction = false,
	tableContext,
	bottomInset = 0,
	readOnly = false,
	onVisibleDatasourceChange = () => {},
	showSelectionCheckboxes = false,
	selectedItems,
	disableBulkOrderActions = false,
	getSelectionAriaLabel = getDefaultSelectionAriaLabel,
}) => {
	const config = useSelector(selectSelectedDataSourceConfig);
	const selectedDataFilterId = useSelector(selectSelectedDataFilterId);
	const selectedSubModule = useSelector(selectSelectedSubModule);
	const profile = useSelector(selectProfile);
	const { theme, isDarkTheme } = useTheme();
	const isOrdersControlTable = isOrdersControlRootTable(
		selectedSubModule,
		tableContext,
	);

	const containerRef = useRef(null);
	const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

	useEffect(() => {
		if (!containerRef.current) return;
		const observer = new ResizeObserver(([entry]) => {
			const { width, height } = entry.contentRect;
			setDimensions({ width, height });
		});
		observer.observe(containerRef.current);
		return () => observer.disconnect();
	}, []);

	const [state, setState] = useState({
		...stateViewer,
		selected: {
			items: Array.isArray(selectedItems) ? selectedItems : [],
		},
		filters: {
			/**
			 * [dataKey]: {
			 *  checkedList: [],
			 *  indeterminate: false,
			 *  checkedAll: false,
			 *  inputSearch: undefined
			 * }
			 */
		},
		sort: {
			dataKey: undefined,
			direction: 'asc',
		},
	});
	const isSelectionControlled =
		showSelectionCheckboxes && Array.isArray(selectedItems);
	const selectionItems = isSelectionControlled
		? selectedItems
		: state.selected.items;

	useEffect(() => {
		setState(current => mergeStateViewerPermissions(current, stateViewer));
	}, [
		stateViewer.isTrasactionOpen,
		stateViewer.isTransactionFrozen,
		stateViewer.isOriginTransaction,
		stateViewer.itemStateOptions,
		stateViewer.itemStateOptionsStatus,
	]);

	useEffect(() => {
		if (isSelectionControlled) return;
		setSelectedItems(state.selected.items);
		setStateItemIds(state.selected.items);
		if (onlyItem && state.selected.items.length > 1)
			setState(current => {
				const currentItems = current.selected.items;

				return {
					...current,
					selected: {
						...current.selected,
						items: currentItems.length ? [currentItems.at(-1)] : [],
					},
				};
			});
	}, [state.selected.items, isSelectionControlled]);

	useEffect(() => {
		if (!isSelectionControlled) return;
		const controlledItems =
			onlyItem && selectionItems.length > 1
				? [selectionItems.at(-1)]
				: selectionItems;
		if (controlledItems !== selectionItems) setSelectedItems(controlledItems);
		setStateItemIds(controlledItems);
	}, [isSelectionControlled, onlyItem, selectionItems]);

	const paginationSource = unsortedDatasource ?? datasource;
	const lastRowId = React.useMemo(
		() => paginationSource.at(-1)?.id,
		[paginationSource],
	);
	const isTotalLastRow = React.useMemo(
		() => Number(lastRowId) === Number(datasourcePaging.totalLastRowId),
		[lastRowId],
	);

	const onRowsRendered = ({ overscanStopIndex, stopIndex }) => {
		if (
			overscanStopIndex + 1 === datasource.length &&
			!isTotalLastRow &&
			setLastRowId
		) {
			setLastRowId(lastRowId);
			if (
				dataFilterLastRowId !== undefined &&
				lastRowId !== dataFilterLastRowId &&
				reloadDatasource
			)
				reloadDatasource();
		}
	};

	const filteredDatasource = React.useMemo(
		() => TableUtils.filter(datasource, state.filters),
		[datasource, state.filters],
	);

	const visibleSelectionIds = React.useMemo(
		() =>
			showSelectionCheckboxes
				? filteredDatasource
						.map(record => record?.[selectionKey])
						.filter(itemId => itemId !== undefined && itemId !== null)
				: [],
		[showSelectionCheckboxes, filteredDatasource, selectionKey],
	);

	useEffect(() => {
		if (!showSelectionCheckboxes) return;
		const availableIds = new Set(visibleSelectionIds);
		const visibleSelection = selectionItems.filter(itemId =>
			availableIds.has(itemId),
		);
		if (visibleSelection.length === selectionItems.length) return;
		if (isSelectionControlled) {
			setSelectedItems(visibleSelection);
			return;
		}
		setState(current => ({
			...current,
			selected: { ...current.selected, items: visibleSelection },
		}));
	}, [
		showSelectionCheckboxes,
		isSelectionControlled,
		selectionItems,
		visibleSelectionIds,
	]);

	// Preserve the legacy orders-control reconciliation without making every
	// shared table pay for the explicit checkbox seam.
	useEffect(() => {
		if (!isOrdersControlTable || showSelectionCheckboxes) return;
		const availableIds = new Set(
			datasource.map(record => record[selectionKey]),
		);
		setState(current => {
			const availableSelection = current.selected.items.filter(itemId =>
				availableIds.has(itemId),
			);
			if (availableSelection.length === current.selected.items.length)
				return current;
			return {
				...current,
				selected: { ...current.selected, items: availableSelection },
			};
		});
	}, [datasource, selectionKey, isOrdersControlTable, showSelectionCheckboxes]);

	useEffect(() => {
		notifyVisibleDatasourceChange(
			onVisibleDatasourceChange,
			filteredDatasource,
		);
	}, [filteredDatasource, onVisibleDatasourceChange]);

	const displayFields = React.useMemo(
		() =>
			prepareOrdersControlFields(
				addPaymentPaycutActionFields(
					addTransactionSignatureActionField(
						datasourceFields,
						selectedSubModule,
						disableTransactionSignatureAction ||
							isPendingTransactionsDatasource(selectedDataFilterId),
					),
					selectedSubModule,
					selectedDataFilterId,
					tableContext,
				),
				selectedSubModule,
				tableContext,
				!disableSelection,
			),
		[
			datasourceFields,
			selectedSubModule,
			disableTransactionSignatureAction,
			selectedDataFilterId,
			tableContext,
			disableSelection,
		],
	);

	const dataColumnsWidth = React.useMemo(
		() => displayFields.reduce((sum, field) => sum + field.width, 0),
		[displayFields],
	);
	const selectionColumnWidth = showSelectionCheckboxes
		? SELECTION_COLUMN_WIDTH
		: 0;
	const totalWidth = dataColumnsWidth + selectionColumnWidth;
	const tableWidth = dimensions.width || totalWidth;
	const availableDataWidth = Math.max(tableWidth - selectionColumnWidth, 0);
	const colScale =
		dataColumnsWidth > 0 ? availableDataWidth / dataColumnsWidth : 1;
	const updateSelectedItems = updater => {
		if (isSelectionControlled) {
			setSelectedItems(
				typeof updater === 'function' ? updater(selectionItems) : updater,
			);
			return;
		}
		setState(current => ({
			...current,
			selected: {
				...current.selected,
				items:
					typeof updater === 'function'
						? updater(current.selected.items)
						: updater,
			},
		}));
	};
	const onToggleAllVisible = checked => {
		const visibleIds = new Set(visibleSelectionIds);
		updateSelectedItems(currentItems =>
			checked && onlyItem
				? visibleSelectionIds.length
					? [visibleSelectionIds[0]]
					: []
				: checked
					? Array.from(new Set([...currentItems, ...visibleSelectionIds]))
					: currentItems.filter(itemId => !visibleIds.has(itemId)),
		);
	};
	const onToggleRow = (itemId, checked) =>
		updateSelectedItems(currentItems =>
			checked && onlyItem
				? [itemId]
				: checked
					? Array.from(new Set([...currentItems, itemId]))
					: currentItems.filter(selectedId => selectedId !== itemId),
		);
	const cellSelection = {
		...state.selected,
		items: showSelectionCheckboxes ? [] : selectionItems,
		itemKey: selectionKey,
	};
	const cellState = showSelectionCheckboxes
		? {
				...state,
				selected: { ...state.selected, items: [] },
			}
		: state;

	return (
		<Wrapper width='100%' padding='0' position='relative'>
			<div ref={containerRef} style={{ width: '100%', height: '100%' }}>
				<ContainerTable
					notHorizontalScroll={config.notHorizontalScroll}
					$bottomInset={bottomInset}
				>
					<Table
						highBackground
						style={{
							backgroundColor: isDarkTheme
								? theme._primaryBackgroundColor
								: theme._secondaryBackgroundColor,
						}}
						width={tableWidth}
						height={dimensions.height}
						headerHeight={40}
						rowHeight={25}
						rowCount={filteredDatasource.length}
						rowGetter={({ index }) => filteredDatasource[index]}
						onRowsRendered={onRowsRendered}
						rowRenderer={props => {
							const rowKey = getStableTableRowKey(
								props.rowData,
								selectionKey,
								props.key,
							);

							return (
								<RowRenderer
									{...props}
									key={rowKey}
									rowKey={rowKey}
									filteredDatasource={filteredDatasource}
									selected={{
										...state.selected,
										items: selectionItems,
										itemKey: selectionKey,
									}}
									setState={setState}
									selectionKey={selectionKey}
									disableSelection={disableSelection || showSelectionCheckboxes}
									isOrdersControlRootTable={isOrdersControlTable}
									isExplicitSelectionTable={showSelectionCheckboxes}
								/>
							);
						}}
					>
						{showSelectionCheckboxes && (
							<Column
								key='__selection__'
								dataKey='__selection__'
								label='Seleccionar'
								width={SELECTION_COLUMN_WIDTH}
								flexGrow={0}
								flexShrink={0}
								cellRenderer={({ rowData }) => {
									const itemId = rowData?.[selectionKey];
									return (
										<SelectionRowCheckbox
											checked={selectionItems.includes(itemId)}
											disabled={
												disableSelection ||
												itemId === undefined ||
												itemId === null
											}
											label={getSelectionAriaLabel(rowData, itemId)}
											onChange={checked => onToggleRow(itemId, checked)}
										/>
									);
								}}
								headerRenderer={() => (
									<SelectionHeaderCheckbox
										selectedItems={selectionItems}
										visibleItemIds={visibleSelectionIds}
										onChange={onToggleAllVisible}
										disabled={disableSelection}
									/>
								)}
							/>
						)}
						{displayFields.map(field => (
							<Column
								key={field.id}
								dataKey={field.id}
								label={field.name}
								width={Math.floor(field.width * colScale)}
								cellRenderer={props => (
									<CellRenderer
										{...props}
										Container={Cell}
										field={field}
										selected={cellSelection}
										fieldActionTypes={fieldActionTypes}
										profile={profile}
										setState={setState}
										stateTable={cellState}
										readOnly={readOnly}
									/>
								)}
								headerRenderer={props => (
									<HeaderRenderer
										{...props}
										field={field}
										datasource={datasource}
										filters={state.filters}
										dateFormat={profile.user.settings.date_format}
										setState={setState}
									/>
								)}
							/>
						))}
					</Table>
				</ContainerTable>
			</div>

			{isLoading && dataFilterLastRowId > 0 && (
				<Wrapper padding='0' position='absolute' right='16px' bottom='16px'>
					<LogoIcon spin={true} size='m' />
				</Wrapper>
			)}

			{!disableBulkOrderActions && (
				<BulkOrderActionsBar
					datasource={datasource}
					datasourceFields={datasourceFields}
					selectedItemIds={selectionItems}
					selectionKey={selectionKey}
					tableContext={tableContext}
				/>
			)}
		</Wrapper>
	);
};

export default StandardTableScreen;
